import React, { useState, useEffect } from 'react';
import {
  parseYoutubeVideoId,
  getYoutubeEmbedSrc,
} from '../../../utils/productMedia';
import './ProductImageGallery.css';

export function ProductImageGallery({ product, admin = false, onClick }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Gallery hero uses product.images only; thumbnail stays image-only for cards/listings.
  const galleryImages =
    product && Array.isArray(product.images) ? product.images : [];

  useEffect(() => {
    setCurrentImageIndex(0);
  }, [product?.id]);

  useEffect(() => {
    if (!galleryImages.length) {
      setCurrentImageIndex(0);
      return;
    }
    setCurrentImageIndex((i) => Math.min(i, galleryImages.length - 1));
  }, [galleryImages.length]);

  if (!product) return null;

  const hasMultipleImages = galleryImages.length > 1;
  const currentUrl = galleryImages.length > 0 ? galleryImages[currentImageIndex] : null;
  const currentYoutubeId = currentUrl ? parseYoutubeVideoId(currentUrl) : null;
  const embedSrc = currentYoutubeId ? getYoutubeEmbedSrc(currentYoutubeId) : null;

  const handlePrevImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : galleryImages.length - 1));
  };

  const handleNextImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev < galleryImages.length - 1 ? prev + 1 : 0));
  };

  const handleDotClick = (e, index) => {
    e.stopPropagation();
    setCurrentImageIndex(index);
  };

  // Calculate which dots to show (max 5-7 with smart indicators)
  const getVisibleDots = () => {
    const totalImages = galleryImages.length;
    const current = currentImageIndex;

    if (totalImages <= 5) {
      // Show all dots if 5 or fewer
      return Array.from({ length: totalImages }, (_, i) => ({
        index: i,
        isSmall: false
      }));
    }

    // For more than 5 images, show slice of 5 with indicators
    const result = [];
    const maxSlice = 5; // Maximum dots in the middle slice
    const halfSlice = Math.floor(maxSlice / 2);

    // Calculate the 5-dot slice around current index
    let sliceStart = Math.max(0, current - halfSlice);
    let sliceEnd = Math.min(totalImages - 1, sliceStart + maxSlice - 1);

    // Adjust slice to keep current index in view
    if (sliceEnd - sliceStart + 1 < maxSlice) {
      if (sliceStart === 0) {
        sliceEnd = Math.min(totalImages - 1, sliceStart + maxSlice - 1);
      } else if (sliceEnd === totalImages - 1) {
        sliceStart = Math.max(0, sliceEnd - maxSlice + 1);
      }
    }

    // Add first indicator dot if there are images before the slice
    if (sliceStart > 0) {
      result.push({ index: 0, isSmall: true });
    }

    // Add the 5-dot slice
    for (let i = sliceStart; i <= sliceEnd; i++) {
      result.push({ index: i, isSmall: false });
    }

    // Add last indicator dot if there are images after the slice
    if (sliceEnd < totalImages - 1) {
      result.push({ index: totalImages - 1, isSmall: true });
    }

    return result;
  };

  return (
    <div className="product-image-gallery">
      {/* Main Image Container - 21:9 aspect ratio */}
      <div className={`product-image-container ${admin ? 'admin-editable' : ''}`} onClick={admin ? onClick : undefined}>
        {currentUrl && embedSrc ? (
          <iframe
            key={`${currentUrl}-${currentImageIndex}`}
            title={`${product.name || 'Product'} video`}
            src={embedSrc}
            className="product-image-main product-image-main--youtube"
            allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
          />
        ) : currentUrl ? (
          <img
            key={currentUrl}
            src={currentUrl}
            alt={`${product.name || 'Product'}${hasMultipleImages ? ` gallery ${currentImageIndex + 1}` : ''}`.trim()}
            className="product-image-main"
            onError={(e) => {
              e.target.src = 'https://via.placeholder.com/1400x600/333/666?text=Image+Not+Available';
            }}
          />
        ) : (
          <div className="product-image-placeholder">
            <i className="fa-solid fa-image"></i>
            <span>No Image Available</span>
          </div>
        )}

        {/* Admin Edit Overlay */}
        {admin && (
          <div className="product-image-edit-overlay">
            <div className="product-image-edit-content">
              <i className="fa-solid fa-edit"></i>
              <span>Edit Images</span>
            </div>
          </div>
        )}

        {/* Navigation Arrows - Only show if multiple images */}
        {hasMultipleImages && (
          <>
            <button
              type="button"
              className="product-image-nav product-image-nav--prev"
              onClick={handlePrevImage}
              aria-label="Previous image"
            >
              <i className="fa-solid fa-chevron-left"></i>
            </button>
            <button
              type="button"
              className="product-image-nav product-image-nav--next"
              onClick={handleNextImage}
              aria-label="Next image"
            >
              <i className="fa-solid fa-chevron-right"></i>
            </button>
          </>
        )}

        {/* Navigation Dots - Positioned over image */}
        {hasMultipleImages && (
          <div className="product-image-dots">
            {getVisibleDots().map((dot) => (
              <button
                type="button"
                key={dot.index}
                className={`product-image-dot ${dot.index === currentImageIndex ? 'active' : ''} ${dot.isSmall ? 'small' : ''}`}
                onClick={(e) => handleDotClick(e, dot.index)}
                aria-label={dot.isSmall ? `Jump to ${dot.index === 0 ? 'first' : 'last'} image` : `Go to gallery image ${dot.index + 1}`}
              />
            ))}
          </div>
        )}

        {/* Image Counter - Positioned over image */}
        {hasMultipleImages && (
          <div className="product-image-counter">
            {currentImageIndex + 1} / {galleryImages.length}
          </div>
        )}
      </div>
    </div>
  );
}
